import { z } from "zod";

// Tiers and statuses

export const TierSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);
export type Tier = z.infer<typeof TierSchema>;

export const ApplicationStatusSchema = z.enum([
  "interested",
  "preparing",
  // The user reviewed the exact packet in /review and approved it; a hashed
  // snapshot is frozen in application_approvals. Not yet submitted.
  "approved",
  "applied",
  "interviewing",
  "offer",
  "rejected",
  "withdrawn",
]);
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>;

export const AtsPlatformSchema = z.enum([
  "greenhouse",
  "ashby",
  "workday",
  "smartrecruiters",
  "lever",
  "oracle_hcm",
  "talentbrew",
  "gem",
  "amazon_jobs",
  "google_careers",
]);
export type AtsPlatform = z.infer<typeof AtsPlatformSchema>;

// Catalog

export const CompanyProfileSchema = z
  .object({
    compensation: z
      .object({
        base_range: z.string().optional(),
        ttc_range: z.string().optional(),
        notes: z.string().optional(),
      })
      .partial()
      .optional(),
    financials: z
      .object({
        revenue: z.string().optional(),
        revenue_growth: z.string().optional(),
        valuation_or_market_cap: z.string().optional(),
        public: z.boolean().optional(),
        stock_ticker: z.string().optional(),
        founded: z.string().optional(),
      })
      .partial()
      .optional(),
    headquarters: z.string().optional(),
    remote_policy: z.string().optional(),
  })
  .partial();
export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;

export const CompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: TierSchema,
  ats: AtsPlatformSchema,
  ats_config: z.record(z.string(), z.unknown()),
  careers_url: z.string().url().nullable().optional(),
  default_keywords: z.array(z.string()),
  priority: z.string().nullable().optional(),
  profile: CompanyProfileSchema.nullable().optional(),
});
export type Company = z.infer<typeof CompanySchema>;

// Jobs (snapshot view)

export const JobPostingSchema = z.object({
  company_id: z.string(),
  job_id: z.string(),
  url: z.string().url(),
  title: z.string(),
  location: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  first_seen_at: z.number().int(),
  last_seen_at: z.number().int(),
  removed_at: z.number().int().nullable().optional(),
});
export type JobPosting = z.infer<typeof JobPostingSchema>;

// Applications

export const ApplicationSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string(),
  url: z.string().url(),
  company_id: z.string().nullable().optional(),
  role: z.string(),
  status: ApplicationStatusSchema,
  listing_active: z.boolean(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type Application = z.infer<typeof ApplicationSchema>;

// Session 5 PR3 — ad-hoc job tracking. The user pastes a URL from a
// non-monitored company; we Browser-Render the page, extract a synthetic
// posting, and route it through the same scoring pipeline. The DB row lives
// in `ad_hoc_jobs` (migration 0019); this is its on-the-wire shape.
export const AdHocJobSchema = z.object({
  company_id: z.string(),       // 'adhoc:<sha1(host)>'
  job_id: z.string(),           // 'aj_<sha1(url)>'
  created_by_user: z.string(),
  url: z.string(),
  title: z.string(),
  location: z.string().nullable(),
  company_name: z.string(),
  source: z.literal("ad_hoc"),
  first_seen_at: z.number().int(),
  last_seen_at: z.number().int(),
  removed_at: z.number().int().nullable(),
});
export type AdHocJob = z.infer<typeof AdHocJobSchema>;

// POST /api/ad-hoc/scrape response — the rendered text of a job posting page,
// lifted by Browser Rendering. Over MCP the connected model reads this and
// extracts {company, role, location, description} itself (no Workers-AI pass),
// then supplies them to create_application. Same shape as the internal
// `ScrapedPage` in lib/extract-adhoc-job.ts.
export const ScrapedJobPageSchema = z.object({
  page_title: z.string(),
  og_title: z.string(),
  og_site_name: z.string(),
  headings: z.array(z.string()),
  text: z.string(),
});
export type ScrapedJobPage = z.infer<typeof ScrapedJobPageSchema>;

// POST /api/applications request body. Backwards-compatible: existing
// callers (Track-by-URL with role + status) keep working. New ad-hoc path
// triggers when (a) the URL doesn't map to a monitored job, AND (b) either the
// caller supplied ad-hoc fields (role + description — the MCP/Claude path, no
// Workers-AI extraction) OR omitted the role (the web paste-URL fallback that
// still Browser-Renders + gemma-extracts), AND (c) `allow_ad_hoc` isn't false.
export const CreateAdHocApplicationRequestSchema = z.object({
  url: z.string().url(),
  status: ApplicationStatusSchema.optional(),
  role: z.string().optional(),
  company_id: z.string().optional(),
  allow_ad_hoc: z.boolean().optional(),
  // Caller-supplied ad-hoc fields (the connected model extracts these from a
  // scrape_job_posting result). When role + description are present the server
  // builds the ad_hoc_jobs row directly — no Browser Rendering, no gemma.
  company_name: z.string().optional(),
  location: z.string().optional(),
  description: z.string().optional(),
});
export type CreateAdHocApplicationRequest = z.infer<
  typeof CreateAdHocApplicationRequestSchema
>;

// AI summaries

export const JobSummarySchema = z.object({
  url: z.string().url(),
  summary: z.string(),
  fit: z.number().int().min(1).max(10),
  strengths: z.array(z.string()),
  gaps: z.array(z.string()),
  verdict: z.string(),
  model: z.string(),
  created_at: z.number().int(),
});
export type JobSummary = z.infer<typeof JobSummarySchema>;

// Cover letters

export const CoverLetterSchema = z.object({
  id: z.string().uuid(),
  url: z.string().url(),
  resume_key: z.string(),
  resume_version_id: z.string().nullable().optional(),
  pdf_key: z.string(),
  text: z.string(),
  model: z.string(),
  created_at: z.number().int(),
});
export type CoverLetter = z.infer<typeof CoverLetterSchema>;

// Check runs

export const CheckRunStatusSchema = z.enum([
  "queued",
  "running",
  "completed",
  "failed",
]);

export const CheckRunSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().nullable(),
  scope: z.string(),
  status: CheckRunStatusSchema,
  started_at: z.number().int(),
  finished_at: z.number().int().nullable(),
  results: z.record(z.string(), z.unknown()).nullable(),
});
export type CheckRun = z.infer<typeof CheckRunSchema>;

// Auth

export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  is_admin: z.boolean(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

// Session 2 — score axes, weights, locations, filters

// Each axis is an integer 1-10. Four come from the model; `location_fit`
// is computed deterministically from job_locations × user prefs (single
// source of truth lives in apps/worker/src/lib/location-match.ts).
export const SCORE_AXES = [
  "responsibilities",
  "seniority",
  "domain",
  "tech_stack",
  "location_fit",
] as const;
export type ScoreAxis = (typeof SCORE_AXES)[number];

export const ScoreAxesSchema = z.object({
  responsibilities: z.number().int().min(1).max(10),
  seniority: z.number().int().min(1).max(10),
  domain: z.number().int().min(1).max(10),
  tech_stack: z.number().int().min(1).max(10),
  location_fit: z.number().int().min(1).max(10),
});
export type ScoreAxes = z.infer<typeof ScoreAxesSchema>;

// Default weights for the composite score. Sum to exactly 1.0. The /settings
// page lets the user tune these with sliders; re-sorting is pure SQL math
// against the denormalized axis columns on `job_scores` — no AI re-inference.
export const DEFAULT_SCORE_WEIGHTS = {
  responsibilities: 0.3,
  seniority: 0.25,
  domain: 0.2,
  tech_stack: 0.15,
  location_fit: 0.1,
} as const;

// Tolerance is intentional — float arithmetic from slider drag won't land
// on exactly 1.0 every time; ±0.01 is plenty for a composite-score weighting.
export const ScoreWeightsSchema = z
  .object({
    responsibilities: z.number().min(0).max(1),
    seniority: z.number().min(0).max(1),
    domain: z.number().min(0).max(1),
    tech_stack: z.number().min(0).max(1),
    location_fit: z.number().min(0).max(1),
  })
  .refine(
    (w) => {
      const sum =
        w.responsibilities + w.seniority + w.domain + w.tech_stack + w.location_fit;
      return Math.abs(sum - 1) <= 0.01;
    },
    { message: "weights must sum to 1.0 (±0.01)" },
  );
export type ScoreWeights = z.infer<typeof ScoreWeightsSchema>;

// One persisted row of `job_scores`. The row is keyed (user, job, resume).
export const JobScoreSchema = z.object({
  user_id: z.string(),
  company_id: z.string(),
  job_id: z.string(),
  resume_id: z.string(),
  score: z.number().int().min(1).max(10),
  axes: ScoreAxesSchema,
  rationale: z.string(),
  model: z.string(),
  created_at: z.number().int(),
});
export type JobScore = z.infer<typeof JobScoreSchema>;

// Work mode as classified by the JD-extraction model. 'unspecified' is
// honest signal — UI surfaces a "Location unclear" badge for these rather
// than pretending we've cleanly classified the row.
export const WorkModeSchema = z.enum(["remote", "hybrid", "onsite", "unspecified"]);
export type WorkMode = z.infer<typeof WorkModeSchema>;

export const OfficeLocationSchema = z.object({
  city: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
});
export type OfficeLocation = z.infer<typeof OfficeLocationSchema>;

// What the model returns when extracting locations from a JD. Persisted as
// the `job_locations` row; `eligible_*`/`office_locations` are arrays in
// memory but stored as JSON strings on the row (see job_locations.ts).
//
// `relocation_offered` / `visa_sponsorship` use 0/1/-1 because SQLite has
// no boolean type and we need a third "unspecified" state.
export const ExtractedJobLocationSchema = z.object({
  mode: WorkModeSchema,
  eligible_countries: z.array(z.string().length(2)),
  eligible_regions: z.array(z.string()),
  eligible_us_states: z.array(z.string().length(2)),
  excluded_us_states: z.array(z.string().length(2)),
  office_locations: z.array(OfficeLocationSchema),
  required_in_office_days: z.string().nullable(),
  travel_pct: z.number().int().min(0).max(100).nullable(),
  relocation_offered: z.union([z.literal(0), z.literal(1), z.literal(-1)]),
  visa_sponsorship: z.union([z.literal(0), z.literal(1), z.literal(-1)]),
  rationale: z.string().max(500),
});
export type ExtractedJobLocation = z.infer<typeof ExtractedJobLocationSchema>;

// Full row shape (extracted facts + provenance). Mirrors the columns on
// the job_locations table. Used by /api/jobs to surface location info.
export const JobLocationSchema = ExtractedJobLocationSchema.extend({
  company_id: z.string(),
  job_id: z.string(),
  model: z.string(),
  extracted_at: z.number().int(),
  text_hash: z.string(),
});
export type JobLocation = z.infer<typeof JobLocationSchema>;

// /api/jobs location filter shape. Mirrored by the <LocationFilter> UI
// component. `eligible_in='me'` resolves on the server to the user's
// primary country (defaults to 'US' until Session 4's user_location_preferences
// lands).
export const LocationFilterSchema = z.object({
  mode: z.array(WorkModeSchema).optional(),
  eligible_in: z
    .union([z.string().length(2), z.literal("me")])
    .optional(),
  excludes_state: z.string().length(2).optional(),
  max_in_office_days: z.number().int().min(0).max(7).optional(),
  max_travel_pct: z.number().int().min(0).max(100).optional(),
  requires_no_visa_sponsorship: z.boolean().optional(),
  requires_relocation_offered: z.boolean().optional(),
});
export type LocationFilter = z.infer<typeof LocationFilterSchema>;

// PATCH /api/me body shape. Both fields are optional — partial updates allowed.
export const UserPrefsSchema = z.object({
  primary_resume_id: z.string().nullable().optional(),
  score_weights: ScoreWeightsSchema.optional(),
});
export type UserPrefs = z.infer<typeof UserPrefsSchema>;

// GET /api/jobs/shortlist query. `n` caps the returned size; the route
// always sorts by composite score, restricts to recently-scored rows, and
// excludes any URL the user is already tracking in `applications`.
export const JobShortlistQuerySchema = z.object({
  n: z.coerce.number().int().min(1).max(50).default(10),
  // Optional est. 3-yr TTC floor (USD/yr); drops rows with no posted pay.
  min_ttc: z.coerce.number().min(0).max(10_000_000).optional(),
});
export type JobShortlistQuery = z.infer<typeof JobShortlistQuerySchema>;

// GET /api/jobs/:companyId/:jobId/similar query. `n` caps the returned
// size; route does Vectorize ANN + bge-reranker against the source job's
// embedding text, drops the source itself from the results.
export const JobSimilarQuerySchema = z.object({
  n: z.coerce.number().int().min(1).max(50).default(10),
});
export type JobSimilarQuery = z.infer<typeof JobSimilarQuerySchema>;

// GET /api/jobs/recommend query — Session 7 PR2 centroid recommendations.
// `n` caps the returned size; when the user has no progressed applications,
// the route returns `{ jobs: [], reason: 'insufficient_data' }` instead of
// a noisy "all jobs" list.
export const JobRecommendQuerySchema = z.object({
  n: z.coerce.number().int().min(1).max(20).default(10),
});
export type JobRecommendQuery = z.infer<typeof JobRecommendQuerySchema>;

// Session 3 — career corpus + tailored resume versions

// The kinds of unit a career corpus stores. Mirrored by the SQL CHECK
// constraint on career_units.kind — 0008_career_units.sql seeds the first
// seven; migration 0038 adds `award` (recognitions: hackathon wins, "SE
// Hero", 100% Club, …) so they retrieve as discrete Recognition units
// instead of being trapped in the summary prose. Migration 0044 adds
// `story` — a STAR narrative nested under a role: depth for cover letters
// and behavioural essay answers, never printed as a resume bullet.
export const CAREER_UNIT_KINDS = [
  "bullet",
  "project",
  "role",
  "skill",
  "summary",
  "education",
  "cert",
  "award",
  "story",
] as const;
export type CareerUnitKind = (typeof CAREER_UNIT_KINDS)[number];

export const CareerUnitKindSchema = z.enum(CAREER_UNIT_KINDS);

// One row of `career_units`. Field shape matches the SQL columns; tags is a
// JSON array of strings stored as TEXT and parsed into the schema's array
// shape on read.
export const CareerUnitSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  kind: CareerUnitKindSchema,
  parent_id: z.string().nullable(),
  title: z.string().nullable(),
  content: z.string(),
  company: z.string().nullable(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  tags: z.array(z.string()),
  vector_id: z.string().nullable(),
  text_hash: z.string().nullable(),
  embedded_at: z.number().int().nullable(),
  source: z.string(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type CareerUnit = z.infer<typeof CareerUnitSchema>;

// One row of `resume_versions`. source_unit_ids is a JSON array of
// career_units.id values used to compose the markdown.
export const ResumeVersionSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  application_id: z.string().nullable(),
  label: z.string(),
  markdown: z.string(),
  pdf_key: z.string().nullable(),
  source_unit_ids: z.array(z.string()),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type ResumeVersion = z.infer<typeof ResumeVersionSchema>;

// POST /api/corpus body — server fills id/user_id/timestamps/vector_id/
// text_hash/embedded_at/source. Source for manual creation is hard-coded
// to 'manual' on the server (the import-from-resume route uses its own
// 'imported_from_resume:<resumeId>' marker).
export const CreateCareerUnitSchema = z.object({
  kind: CareerUnitKindSchema,
  parent_id: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  content: z.string().min(1).max(10_000),
  company: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  tags: z.array(z.string()).max(10).optional(),
});
export type CreateCareerUnit = z.infer<typeof CreateCareerUnitSchema>;

// PATCH /api/corpus/:id body — every field optional, but at least one
// must be present (otherwise the call is a no-op and we'd rather 400 than
// silently succeed). Content edits trigger re-embed in updateUnit + the
// route enqueues a CareerProcessMessage.
export const UpdateCareerUnitPatchSchema = z
  .object({
    kind: CareerUnitKindSchema.optional(),
    parent_id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    content: z.string().min(1).max(10_000).optional(),
    company: z.string().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    tags: z.array(z.string()).max(10).optional(),
  })
  .refine((p) => Object.keys(p).length > 0, {
    message: "patch must include at least one field",
  });
export type UpdateCareerUnitPatch = z.infer<typeof UpdateCareerUnitPatchSchema>;

// POST /api/resume-versions/tailor body. Wave 1 locked `job_id` + the
// optional fields. Wave 4 added `company_id` (jobs are keyed by the
// composite (company_id, job_id) so a bare job_id can't identify a row)
// and `excluded_unit_ids` (the swap-out flow re-runs retrieval with a
// dropped unit excluded). Both new fields are required-ish but stay
// backwards compatible: `excluded_unit_ids` defaults to empty.
export const TailorRequestSchema = z.object({
  company_id: z.string(),
  job_id: z.string(),
  application_id: z.string().optional(),
  target_length: z.enum(["one_page", "two_page"]).optional(),
  focus: z.string().optional(),
  excluded_unit_ids: z.array(z.string()).optional(),
  // Career-unit ids the caller pins into availableUnitIds regardless of
  // per-section rerank rank (retrieval-stability guard — see tailor.ts).
  must_include_unit_ids: z.array(z.string()).optional(),
});
export type TailorRequest = z.infer<typeof TailorRequestSchema>;

// POST /api/resume-versions/prepare body (MCP prepare_resume_tailor). The
// posting resolves from catalog identity (company_id + job_id), a URL
// (catalog or the user's ad-hoc row), or an application's URL — at least one
// is required (checked in the route; no .refine so the MCP tool's JSON
// schema stays a plain object). The prompt carries the COMPLETE career
// corpus, so every unit is citable; must_include_unit_ids is accepted for
// old callers and ignored.
export const PrepareTailorRequestSchema = z.object({
  company_id: z.string().optional(),
  job_id: z.string().optional(),
  url: z.string().min(1).optional(),
  application_id: z.string().optional(),
  focus: z.string().optional(),
  excluded_unit_ids: z.array(z.string()).optional(),
  must_include_unit_ids: z.array(z.string()).optional(),
});
export type PrepareTailorRequest = z.infer<typeof PrepareTailorRequestSchema>;

// POST /api/resume-versions/:id/swap body — re-runs the tailor for the
// supplied job with the dropped unit excluded from retrieval. Caller must
// pass (company_id, job_id) because resume_versions doesn't persist the
// JD identity directly — the workshop UI always knows it.
export const ResumeVersionSwapRequestSchema = z.object({
  company_id: z.string(),
  job_id: z.string(),
  unit_id_to_drop: z.string(),
});
export type ResumeVersionSwapRequest = z.infer<
  typeof ResumeVersionSwapRequestSchema
>;

// PATCH /api/resume-versions/:id body — markdown edit + optional label edit.
// Both fields optional but at least one must be present.
export const ResumeVersionPatchSchema = z
  .object({
    markdown: z.string().min(1).optional(),
    label: z.string().min(1).max(120).optional(),
    // Revised citations for an edited markdown; each must be one of the
    // user's own corpus units. Omitted = keep the saved citations.
    source_unit_ids: z.array(z.string()).min(1).optional(),
    // A markdown edit runs the same quality gate as /save. The web
    // workshop sends true (a human editing their own resume is the
    // authority; the approval audit still re-checks it).
    skip_quality_gate: z.boolean().optional(),
  })
  .refine((p) => p.markdown !== undefined || p.label !== undefined || p.source_unit_ids !== undefined, {
    message: "patch must include at least one field",
  });
export type ResumeVersionPatch = z.infer<typeof ResumeVersionPatchSchema>;

// What the import-from-resume Sonnet call returns. Permissive on optional
// scalar fields (Sonnet sometimes returns null even when the prompt says
// "empty string") — we coerce to "" so downstream insert logic doesn't
// branch on null. Bullets/skills must come back as string arrays; empty
// arrays mean "section absent", which is fine.
const optString = z.string().nullish().transform((v): string => v ?? "");

export const ExtractedResumeRoleSchema = z.object({
  title: z.string().min(1),
  company: optString,
  start_date: optString,
  end_date: optString,
  bullets: z.array(z.string()).default([]),
});
export type ExtractedResumeRole = z.infer<typeof ExtractedResumeRoleSchema>;

export const ExtractedResumeProjectSchema = z.object({
  title: z.string().min(1),
  description: z.string().default(""),
  technologies: z.array(z.string()).default([]),
});
export type ExtractedResumeProject = z.infer<typeof ExtractedResumeProjectSchema>;

export const ExtractedResumeEducationSchema = z.object({
  school: z.string().min(1),
  degree: optString,
  field: optString,
  start_date: optString,
  end_date: optString,
});
export type ExtractedResumeEducation = z.infer<typeof ExtractedResumeEducationSchema>;

export const ExtractedResumeCertSchema = z.object({
  name: z.string().min(1),
  issuer: optString,
  date: optString,
});
export type ExtractedResumeCert = z.infer<typeof ExtractedResumeCertSchema>;

// Awards / honors / recognitions (hackathon wins, "SE Hero", 100% Club,
// consecutive top performance ratings). Distinct from certs — no issuing
// exam body — so they become their own `award` career units.
export const ExtractedResumeAwardSchema = z.object({
  name: z.string().min(1),
  issuer: optString,
  date: optString,
});
export type ExtractedResumeAward = z.infer<typeof ExtractedResumeAwardSchema>;

// Top-level shape Sonnet must return for POST /api/corpus/import-from-resume.
// Every collection defaults to [] so a partial extraction parses cleanly.
export const ExtractedResumeSchema = z.object({
  summary: z.string().nullish().transform((v): string => v ?? ""),
  roles: z.array(ExtractedResumeRoleSchema).default([]),
  projects: z.array(ExtractedResumeProjectSchema).default([]),
  skills: z.array(z.string()).default([]),
  education: z.array(ExtractedResumeEducationSchema).default([]),
  certifications: z.array(ExtractedResumeCertSchema).default([]),
  awards: z.array(ExtractedResumeAwardSchema).default([]),
});
export type ExtractedResume = z.infer<typeof ExtractedResumeSchema>;

// Session 4 — User profile + location preferences.
//
// `user_profile` keeps personal/demographic/links/auth fields used to pre-fill
// application forms. Demographics are nullable because "decline to state" is
// the right default until the user picks. `requires_sponsorship` is a tri-
// state (true / false / null=unspecified) so we can tell "user actively said
// no" from "user hasn't filled this in yet".

export const UserProfileSchema = z.object({
  user_id: z.string(),
  full_name: z.string().nullable(),
  phone: z.string().nullable(),
  contact_email: z.string().nullable(),
  city_state: z.string().nullable(),
  salary_expectation: z.string().nullable(),
  gender: z.string().nullable(),
  race_ethnicity: z.string().nullable(),
  veteran_status: z.string().nullable(),
  disability_status: z.string().nullable(),
  lgbtq_status: z.string().nullable(),
  linkedin_url: z.string().nullable(),
  github_url: z.string().nullable(),
  portfolio_url: z.string().nullable(),
  website_url: z.string().nullable(),
  work_authorization: z.string().nullable(),
  requires_sponsorship: z.boolean().nullable(),
  updated_at: z.number().int(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// PATCH /api/profile body — every field optional, but at least one present.
// Server stamps user_id + updated_at. Empty string → null (the form clears
// a field by submitting "" rather than literal null).
//
// `.optional()` is applied at the object level so a missing key stays
// absent in the parsed output (rather than transforming to `null`); that
// keeps the empty-patch refine reliable.
const optStr = z
  .string()
  .nullable()
  .transform((v) => (v == null || v === "" ? null : v));

export const UserProfilePatchSchema = z
  .object({
    full_name: optStr.optional(),
    phone: optStr.optional(),
    contact_email: optStr.optional(),
    city_state: optStr.optional(),
    salary_expectation: optStr.optional(),
    gender: optStr.optional(),
    race_ethnicity: optStr.optional(),
    veteran_status: optStr.optional(),
    disability_status: optStr.optional(),
    lgbtq_status: optStr.optional(),
    linkedin_url: optStr.optional(),
    github_url: optStr.optional(),
    portfolio_url: optStr.optional(),
    website_url: optStr.optional(),
    work_authorization: optStr.optional(),
    requires_sponsorship: z.boolean().nullable().optional(),
  })
  .refine((p) => Object.keys(p).length > 0, {
    message: "patch must include at least one field",
  });
export type UserProfilePatch = z.infer<typeof UserProfilePatchSchema>;

// `user_location_preferences` is the source of truth for /jobs filter
// `eligible_in='me'` and the location_fit score axis. PATCH triggers a
// synchronous pure-SQL recompute of every job_scores row owned by the user.

export const RelocateToTargetSchema = z.object({
  city: z.string().min(1),
  country: z.string().length(2), // ISO alpha-2
});
export type RelocateToTarget = z.infer<typeof RelocateToTargetSchema>;

export const UserLocationPreferencesSchema = z.object({
  user_id: z.string(),
  primary_country: z.string().length(2),
  primary_state: z.string().nullable(),
  primary_city: z.string().nullable(),
  primary_timezone: z.string().nullable(),
  eligible_countries: z.array(z.string().length(2)),
  open_to_remote: z.boolean(),
  open_to_hybrid: z.boolean(),
  open_to_onsite: z.boolean(),
  max_in_office_days: z.number().int().min(0).max(7).nullable(),
  max_travel_pct: z.number().int().min(0).max(100).nullable(),
  willing_to_relocate: z.boolean(),
  relocate_to: z.array(RelocateToTargetSchema),
  relocation_must_be_paid: z.boolean(),
  timezone_overlap_hours_min: z.number().int().min(0).max(24).nullable(),
  updated_at: z.number().int(),
});
export type UserLocationPreferences = z.infer<typeof UserLocationPreferencesSchema>;

// PATCH /api/profile/location body — full replace (the UI submits the
// complete form on save) but every field is optional so partial updates
// are also accepted. user_id + updated_at are server-stamped.
export const UserLocationPreferencesPatchSchema = z
  .object({
    primary_country: z.string().length(2).optional(),
    primary_state: z.string().nullable().optional(),
    primary_city: z.string().nullable().optional(),
    primary_timezone: z.string().nullable().optional(),
    eligible_countries: z.array(z.string().length(2)).optional(),
    open_to_remote: z.boolean().optional(),
    open_to_hybrid: z.boolean().optional(),
    open_to_onsite: z.boolean().optional(),
    max_in_office_days: z.number().int().min(0).max(7).nullable().optional(),
    max_travel_pct: z.number().int().min(0).max(100).nullable().optional(),
    willing_to_relocate: z.boolean().optional(),
    relocate_to: z.array(RelocateToTargetSchema).optional(),
    relocation_must_be_paid: z.boolean().optional(),
    timezone_overlap_hours_min: z.number().int().min(0).max(24).nullable().optional(),
  })
  .refine((p) => Object.keys(p).length > 0, {
    message: "patch must include at least one field",
  });
export type UserLocationPreferencesPatch = z.infer<
  typeof UserLocationPreferencesPatchSchema
>;

// Session 4 PR2 — Application assistant.
//
// Form-field extraction returns a list of these. `name` is the form's
// HTML `name` attribute (the key the user copies into the field); `label`
// is the visible text. `options` is populated for select / radio fields.
// `raw_html_id` is the DOM id (for deep-linking back to the page).

export const APPLICATION_FIELD_TYPES = [
  "text",
  "textarea",
  "email",
  "tel",
  "url",
  "number",
  "select",
  "radio",
  "checkbox",
  "file",
  "date",
  "unknown",
] as const;
export type ApplicationFieldType = (typeof APPLICATION_FIELD_TYPES)[number];

export const ApplicationFieldTypeSchema = z.enum(APPLICATION_FIELD_TYPES);

export const ExtractedFieldSchema = z.object({
  label: z.string(),
  name: z.string(),
  type: ApplicationFieldTypeSchema,
  required: z.boolean(),
  options: z.array(z.string()).optional(),
  raw_html_id: z.string().nullable().optional(),
  // The form's own help text under the question, when it publishes one.
  // Frequently load-bearing — Anthropic's "Why Anthropic?" carries "great
  // answers are often 200-400 words", and an arbitration checkbox carries the
  // agreement itself. Kept out of `label` so label-keyed matching (the
  // recurring-answer bank, prior answers) stays on the question alone.
  help_text: z.string().optional(),
  // Workday wizard fields (docs/WORKDAY_FILL.md): the wizard step the field
  // sits on ("My Information", "Application Questions", …), the element's
  // data-automation-id when it has a stable one, and the widget the fill
  // tool drives (dropdown, multiselect, radio, checkbox, date, phone, …).
  step: z.string().optional(),
  automation_id: z.string().optional(),
  widget: z.string().optional(),
});
export type ExtractedField = z.infer<typeof ExtractedFieldSchema>;

export const APPLICATION_EXTRACTION_SOURCES = [
  "greenhouse_api", // boards-api questions endpoint — exact, no rendering
  "ashby_api", // jobs.ashbyhq.com non-user GraphQL ApiJobPosting — exact
  "browser", // Browser Rendering DOM walk
  "manual", // register_form_fields (user pasted the questions)
  "workday_discover", // apps/fill discover pass over a signed-in Workday wizard
] as const;
export const ApplicationExtractionSourceSchema = z.enum(
  APPLICATION_EXTRACTION_SOURCES,
);
export type ApplicationExtractionSource = z.infer<
  typeof ApplicationExtractionSourceSchema
>;

export const ApplicationExtractionSchema = z.object({
  fields: z.array(ExtractedFieldSchema),
  // Surface set when Browser Rendering returned suspiciously few fields,
  // labels that look like placeholders (Workday + a few others virtualize
  // their forms hard), or a field set that isn't an application form at all
  // (no file upload — see `degraded_reason`).
  degraded: z.boolean().optional(),
  // Why `degraded` is true. Lets the caller tell "Workday virtualized the
  // form" (paste the questions) from "we scraped a cookie banner" (the URL
  // is wrong, or the form hydrates client-side).
  degraded_reason: z.string().optional(),
  // Which extractor produced these fields.
  source: ApplicationExtractionSourceSchema.optional(),
  // Workday: the real /apply wizard URL (the application's own url is often
  // a TalentBrew careers page) and the tenant + questionnaire it was read from.
  form_url: z.string().optional(),
  workday: z
    .object({
      tenant: z.string(),
      questionnaire_ids: z.array(z.string()),
      // Wizard steps the discover pass actually read; the rest came from the
      // standard template (lib/workday-template.ts).
      discovered_steps: z.array(z.string()),
    })
    .optional(),
});
export type ApplicationExtraction = z.infer<typeof ApplicationExtractionSchema>;

// POST /api/apply/:applicationId/discovered-fields — what the fill tool's
// discover pass read off a signed-in Workday wizard. There is deliberately no
// value field: discovery captures questions, never answers.
export const DiscoveredFieldInputSchema = z.object({
  step: z.string().min(1).max(80),
  label: z.string().min(1).max(500),
  automation_id: z.string().max(200).nullable().optional(),
  widget: z.string().min(1).max(40),
  required: z.boolean(),
  options: z.array(z.string().min(1).max(300)).max(400).optional(),
});
export type DiscoveredFieldInput = z.infer<typeof DiscoveredFieldInputSchema>;

export const DiscoveredFieldsRequestSchema = z.object({
  form_url: z.string().url(),
  tenant: z.string().min(1).max(100),
  questionnaire_ids: z.array(z.string().max(100)).max(4).default([]),
  // Steps the pass reached and read in full.
  steps: z.array(z.string().min(1).max(80)).min(1).max(12),
  fields: z.array(DiscoveredFieldInputSchema).max(150),
});
export type DiscoveredFieldsRequest = z.infer<typeof DiscoveredFieldsRequestSchema>;

// Field classifier output. Mirrors the SQL CHECK on application_answers.field_kind.
export const APPLICATION_FIELD_KINDS = [
  "personal",
  "links",
  "auth",
  "compensation",
  "demographic",
  "experience",
  "essay",
  "unknown",
] as const;
export type ApplicationFieldKind = (typeof APPLICATION_FIELD_KINDS)[number];
export const ApplicationFieldKindSchema = z.enum(APPLICATION_FIELD_KINDS);

export const ClassifiedFieldSchema = ExtractedFieldSchema.extend({
  kind: ApplicationFieldKindSchema,
  // The profile key (or sub-key) the prefill should pull from. Null for
  // essay/unknown — those route through the generator instead.
  profile_key: z.string().nullable(),
  // Drafted only for essay fields; the workshop UI calls /answer per essay.
  needs_generation: z.boolean(),
});
export type ClassifiedField = z.infer<typeof ClassifiedFieldSchema>;

// One row of `application_answers`.
export const ApplicationAnswerSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  user_id: z.string(),
  field_name: z.string(),
  field_label: z.string(),
  field_kind: ApplicationFieldKindSchema,
  answer: z.string(),
  user_edited: z.boolean(),
  accepted_at: z.number().int().nullable(),
  sources: z.array(z.string()),
  // Who authored the stored text: a Sonnet model id (web path), 'mcp:client'
  // (MCP save_application_answers), 'mcp:user' (dictated). Null = pre-0031.
  model: z.string().nullable(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type ApplicationAnswer = z.infer<typeof ApplicationAnswerSchema>;

// POST /api/apply/extract body. Exactly one of application_url /
// application_id — the route resolves application_id to the caller's own
// application row's URL. `force` bypasses the cache read (still writes
// the fresh extraction through).
export const ExtractApplicationRequestSchema = z
  .object({
    application_url: z.string().url().optional(),
    application_id: z.string().optional(),
    force: z.boolean().optional(),
    // Optional cap on extraction TTL. Default 24h matches the migration.
    ttl_hours: z.number().int().min(1).max(168).optional(),
  })
  .refine((v) => Boolean(v.application_url) !== Boolean(v.application_id), {
    message: "provide exactly one of application_url or application_id",
  });
export type ExtractApplicationRequest = z.infer<typeof ExtractApplicationRequestSchema>;

// POST /api/apply/answer body. application_id ties the draft to the row;
// field is the classifier output the workshop UI passes through.
export const GenerateAnswerRequestSchema = z.object({
  application_id: z.string(),
  field: ClassifiedFieldSchema,
  // Optional JD text the generator can lean on. The route looks it up via
  // the application row's URL match against jobs / job_locations.
  job_description: z.string().optional(),
});
export type GenerateAnswerRequest = z.infer<typeof GenerateAnswerRequestSchema>;

// PATCH /api/apply/answer/:id body — user edits to a draft.
export const PatchAnswerRequestSchema = z.object({
  answer: z.string().min(1),
});
export type PatchAnswerRequest = z.infer<typeof PatchAnswerRequestSchema>;

// POST /api/apply/:applicationId/answers/set body (APPLY_FLOW_SPRINTS PR4)
// — a dictated answer for any extracted field, including essays (sanctioned
// escape hatch: "tell them Jane referred me"). Stored model:'mcp:user',
// user_edited:1, no sources.
export const SetAnswerRequestSchema = z.object({
  field_name: z.string(),
  answer: z.string().min(1),
});
export type SetAnswerRequest = z.infer<typeof SetAnswerRequestSchema>;

// POST /api/apply/:applicationId/answers/set-batch body. Same semantics as
// set, many fields at once, one kit assembly at the end (per-call kit
// assembly made 16 single sets overrun code_mode's budget). All-or-nothing
// validation: an unknown or duplicate field rejects the whole batch.
export const SetAnswersBatchRequestSchema = z.object({
  answers: z.array(SetAnswerRequestSchema).min(1).max(60),
});
export type SetAnswersBatchRequest = z.infer<typeof SetAnswersBatchRequestSchema>;

// POST /api/apply/:applicationId/prepare-answers body (APPLY_FLOW_SPRINTS
// PR3). Omitting `fields` targets every needs_answer field in the kit
// (essay/experience without a stored answer), capped at 10.
export const PrepareAnswersRequestSchema = z.object({
  fields: z.array(z.string()).min(1).max(10).optional(),
});
export type PrepareAnswersRequest = z.infer<typeof PrepareAnswersRequestSchema>;

// POST /api/apply/:applicationId/register-fields (APPLY_FLOW_SPRINTS PR7).
// Manual fallback for forms Browser Rendering can't read (virtualized
// Workday): the user pastes the form's questions; `name` slugs and types
// are inferred server-side when not given.
export const RegisterFieldInputSchema = z.object({
  label: z.string().min(1).max(300),
  type: ApplicationFieldTypeSchema.optional(),
  required: z.boolean().optional(),
  options: z.array(z.string().min(1)).max(50).optional(),
});
export type RegisterFieldInput = z.infer<typeof RegisterFieldInputSchema>;

export const RegisterFieldsRequestSchema = z.object({
  fields: z.array(RegisterFieldInputSchema).min(1).max(60),
});
export type RegisterFieldsRequest = z.infer<typeof RegisterFieldsRequestSchema>;

// POST /api/apply/answers/save body — the save half of the prepare/save
// generative inversion for application answers. The prepare token pins the
// application + field set + allowed source ids.
export const SaveAnswersRequestSchema = z.object({
  prepare_token: z.string(),
  answers: z
    .array(
      z.object({
        field_name: z.string(),
        answer: z.string().min(1),
        sources: z.array(z.string()).optional(),
      }),
    )
    .min(1)
    .max(10),
});
export type SaveAnswersRequest = z.infer<typeof SaveAnswersRequestSchema>;

// ── Apply kit (APPLY_FLOW_SPRINTS PR1) ─────────────────────────────────────
//
// One consolidated, ordered, copy-paste-ready view of an application's form:
// every extracted field merged with its profile prefill and any drafted
// answer. `value` is THE copy-paste value (answer wins over prefill).
// Served by GET /api/apply/:applicationId/kit and the get_apply_kit MCP tool.

// Resolve a classified field's prefill from the user profile. Lives in
// shared because both the worker (kit assembly) and the web workshop UI
// render the same prefill. Synthesized pseudo-keys: first_name / last_name
// split out of full_name; requires_sponsorship maps to Yes/No; us_state is
// the full state name from city_state's trailing two-letter code; city is
// city_state before the comma.
const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts",
  MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico",
  NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

export function prefillFromProfile(profile: UserProfile, key: string): string | null {
  if (key === "first_name") {
    const parts = (profile.full_name ?? "").trim().split(/\s+/);
    return parts[0] || null;
  }
  if (key === "last_name") {
    const parts = (profile.full_name ?? "").trim().split(/\s+/);
    return parts.length > 1 ? parts.slice(1).join(" ") : null;
  }
  if (key === "city") {
    const city = (profile.city_state ?? "").split(",")[0]?.trim();
    return city || null;
  }
  if (key === "us_state") {
    const m = (profile.city_state ?? "").trim().match(/,\s*([A-Za-z]{2})\s*$/);
    const code = m?.[1]?.toUpperCase();
    return code ? (US_STATE_NAMES[code] ?? code) : null;
  }
  if (key === "requires_sponsorship") {
    if (profile.requires_sponsorship === null) return null;
    return profile.requires_sponsorship ? "Yes" : "No";
  }
  const v = (profile as unknown as Record<string, unknown>)[key];
  if (typeof v === "string" && v.trim()) return v;
  return null;
}

export const APPLY_KIT_FIELD_STATUSES = [
  "ready", // has a copy-paste value (answer or prefill)
  "needs_answer", // essay/experience field with no draft yet → prepare/save loop
  "needs_profile", // profile-keyed field whose profile value is empty → update_profile
  "manual", // file uploads, captchas, anything only the user can do in-browser
] as const;
export type ApplyKitFieldStatus = (typeof APPLY_KIT_FIELD_STATUSES)[number];
export const ApplyKitFieldStatusSchema = z.enum(APPLY_KIT_FIELD_STATUSES);

// The drafted-answer slice carried per kit field (subset of ApplicationAnswer).
export const ApplyKitAnswerSchema = ApplicationAnswerSchema.pick({
  id: true,
  answer: true,
  user_edited: true,
  accepted_at: true,
  sources: true,
});
export type ApplyKitAnswer = z.infer<typeof ApplyKitAnswerSchema>;

// One of the user's prior ACCEPTED answers at the SAME company on an earlier
// application — cross-application reuse. The apply kit / prepare-answers
// context surface these as a raw list (deduped by normalized label); the
// connected model decides which (if any) fits each field and reuses/adapts it
// rather than composing from scratch. No server-side similarity is computed —
// matching is the model's job now (it reads the questions directly).
export const PriorAnswerSchema = z.object({
  field_label: z.string(),
  answer: z.string(),
  from_application_id: z.string().nullable(),
  field_kind: z.string(),
});
export type PriorAnswer = z.infer<typeof PriorAnswerSchema>;

export const APPLY_KIT_PROVENANCES = [
  "answer", // a drafted or user-set answer row for this application
  "profile",
  "standing_answer",
  "company_answer",
  "none",
] as const;
export const ApplyKitProvenanceSchema = z.enum(APPLY_KIT_PROVENANCES);
export type ApplyKitProvenance = z.infer<typeof ApplyKitProvenanceSchema>;

export const APPLY_KIT_ATTENTIONS = ["blocker", "risk", "review", "none"] as const;
export const ApplyKitAttentionSchema = z.enum(APPLY_KIT_ATTENTIONS);
export type ApplyKitAttention = z.infer<typeof ApplyKitAttentionSchema>;

export const ApplyKitFieldSchema = ClassifiedFieldSchema.extend({
  prefill_value: z.string().nullable(),
  answer: ApplyKitAnswerSchema.nullable(),
  value: z.string().nullable(),
  status: ApplyKitFieldStatusSchema,
  // Set only on option-constrained fields (select/radio) whose profile or
  // standing-bank prefill does not match any of the field's own options —
  // the free text we know, which the form cannot accept verbatim. The field
  // reports needs_answer; pick the option this implies.
  unreconciled_prefill: z.string().optional(),
  // Where `value` came from — lets the review queue collapse values the user
  // has already approved elsewhere (profile, standing/company banks) and
  // expand the ones they haven't.
  provenance: ApplyKitProvenanceSchema,
  // What the reviewer should do with this field: `blocker` (required and not
  // ready), `risk` (a legal/eligibility/comp answer where a wrong value hurts),
  // `review` (new prose drafted for this application), or `none`.
  attention: ApplyKitAttentionSchema,
  // Set only on a drafted answer that fails the claim checks (a retracted
  // phrase, a total-years claim past the dated window, a mention of the
  // drafting tool). Such a field is an approval blocker until rewritten.
  audit_issues: z.array(z.string()).optional(),
});
export type ApplyKitField = z.infer<typeof ApplyKitFieldSchema>;

// A copy-paste section: an ordered group of fields under a heading
// (Personal / Experience / Essays / Demographic / Other). `field_names`
// references entries in the flat `fields[]` in form order — sections impose
// grouping + order only, they don't duplicate the field payload. Empty
// sections are omitted. The worker derives section membership from each
// field's classified `kind` (see lib/apply-kit.ts).
export const ApplyKitSectionSchema = z.object({
  id: z.string(),
  label: z.string(),
  field_names: z.array(z.string()),
});
export type ApplyKitSection = z.infer<typeof ApplyKitSectionSchema>;

// Document pointers (resume versions / cover letters tied to the
// application). pdf_url is absolute; label is null for cover letters.
export const ApplyKitDocumentSchema = z.object({
  id: z.string(),
  label: z.string().nullable(),
  created_at: z.number().int(),
  pdf_url: z.string(),
});
export type ApplyKitDocument = z.infer<typeof ApplyKitDocumentSchema>;

// Re-lint of a saved document against the CURRENT corpus + presentation
// rules (lib/document-audit.ts). A failing pinned document blocks approval:
// the corpus corrected a fact the document still prints.
export const DocumentAuditSchema = z.object({
  kind: z.enum(["resume_version", "cover_letter"]),
  id: z.string(),
  ok: z.boolean(),
  issues: z.array(
    z.object({ code: z.string(), severity: z.enum(["error", "warn"]), detail: z.string() }),
  ),
  missing_unit_ids: z.array(z.string()).optional(),
});
export type DocumentAudit = z.infer<typeof DocumentAuditSchema>;

// Warm-path lane (SUBMISSION_FLOW_SPRINTS PR D): who the user knows at the
// application's company and when they last reached out. A touch inside the
// window counts as a live warm path.
export const WARM_TOUCH_WINDOW_DAYS = 30;

export const WarmPathContactSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  role: z.string().nullable(),
  linkedin_url: z.string().nullable(),
  last_contacted_at: z.number().int().nullable(),
  // Explicitly linked to THIS application (vs. matched by company).
  linked: z.boolean(),
});
export type WarmPathContact = z.infer<typeof WarmPathContactSchema>;

export const WarmPathSchema = z.object({
  // companies.tier for catalog applications; null for ad-hoc.
  company_tier: z.number().int().nullable(),
  contacts: z.array(WarmPathContactSchema),
  last_touch: z.number().int().nullable(),
  // last_touch falls inside WARM_TOUCH_WINDOW_DAYS.
  recent: z.boolean(),
});
export type WarmPath = z.infer<typeof WarmPathSchema>;

export const ApplyKitSchema = z.object({
  application: ApplicationSchema,
  // Absolute link to this packet's /review page, where the user reads,
  // edits, and approves it. Not part of the approval snapshot.
  review_url: z.string(),
  // null when no extraction exists yet (call extract_application_form).
  extraction: z
    .object({
      present: z.boolean(),
      degraded: z.boolean(),
      extracted_at: z.number().int().nullable(),
      source: ApplicationExtractionSourceSchema,
    })
    .nullable(),
  fields: z.array(ApplyKitFieldSchema),
  // The user's prior accepted answers at THIS company (deduped by label,
  // newest per label). Reuse-context for the essay/experience fields — the
  // model reads these and decides which fits each un-answered field. Empty
  // when the company is unresolvable or there's nothing to reuse.
  prior_answers: z.array(PriorAnswerSchema),
  // Same fields as `fields`, grouped + ordered into copy-paste sections.
  sections: z.array(ApplyKitSectionSchema),
  // The whole form rendered as one copy-paste-ready block: labeled sections,
  // each field as `Label:\n<value>`, with [markers] for fields the user must
  // still resolve (draft an answer / fill profile / upload in browser).
  paste_sheet: z.string(),
  documents: z.object({
    resume_versions: z.array(ApplyKitDocumentSchema),
    cover_letters: z.array(ApplyKitDocumentSchema),
  }),
  // Audit of the PINNED documents (newest version + newest letter). Any
  // `ok: false` entry is an approval blocker — recompose that document.
  document_audit: z.array(DocumentAuditSchema),
  completion: z.object({
    total: z.number().int(),
    ready: z.number().int(),
    required_missing: z.number().int(),
  }),
  // Hash of the packet exactly as it stands now (see lib/approval.ts). The
  // review UI sends it back on approve so the user approves what they saw.
  snapshot_sha256: z.string(),
  // The latest live approval, or null. `stale` = something changed since the
  // user approved (answer edit, new document, profile change) — re-review.
  approval: z
    .object({
      id: z.string(),
      approved_at: z.number().int(),
      snapshot_sha256: z.string(),
      stale: z.boolean(),
      submitted_at: z.number().int().nullable(),
      submit_mode: z.string().nullable(),
    })
    .nullable(),
  // Not part of the approval snapshot: naming or messaging a contact must
  // not make an approval stale.
  warm_path: WarmPathSchema,
});
export type ApplyKit = z.infer<typeof ApplyKitSchema>;

// The frozen packet an approval covers. Canonical shape — hashed as JSON with
// fields in form order. Document ids pin which PDFs go in the upload slots.
export const PacketSnapshotSchema = z.object({
  application_id: z.string(),
  url: z.string(),
  // Present only for forms that live elsewhere (Workday's /apply wizard), so
  // snapshots without one hash exactly as they did before it existed.
  form_url: z.string().optional(),
  fields: z.array(
    z.object({
      name: z.string(),
      label: z.string(),
      type: z.string(),
      required: z.boolean(),
      value: z.string().nullable(),
      provenance: ApplyKitProvenanceSchema,
    }),
  ),
  documents: z.object({
    resume_version_id: z.string().nullable(),
    resume_version_updated_at: z.number().int().nullable(),
    cover_letter_id: z.string().nullable(),
  }),
});
export type PacketSnapshot = z.infer<typeof PacketSnapshotSchema>;

export const SUBMIT_MODES = [
  "manual", // user copy-pasted and clicked Submit (L1)
  "fill_and_stop", // Claude filled the form in the user's browser; user clicked Submit (L2)
  "assisted_submit", // Claude clicked Submit after the user's explicit go (L3)
] as const;
export const SubmitModeSchema = z.enum(SUBMIT_MODES);
export type SubmitMode = z.infer<typeof SubmitModeSchema>;

// GET /api/apply/review-queue — one row per packet in preparing/approved,
// with the counts the /review list needs to prioritise.
export const ReviewQueueItemSchema = z.object({
  application_id: z.string(),
  review_url: z.string(),
  role: z.string(),
  company_name: z.string(),
  url: z.string(),
  status: ApplicationStatusSchema,
  updated_at: z.number().int(),
  // Clef expected fit (0-4); null for ad-hoc or unqualified postings.
  // Optional: a payload cached before the field existed has none.
  expected_fit: z.number().nullable().optional(),
  // `pnpm fill` can put this packet into the form (Greenhouse, Ashby).
  autofill: z.boolean().optional(),
  extracted: z.boolean(),
  counts: z.object({
    fields: z.number().int(),
    blockers: z.number().int(),
    risk: z.number().int(),
    review: z.number().int(),
  }),
  // Pinned documents that fail the audit against the current corpus.
  stale_documents: z.number().int(),
  has_resume_version: z.boolean(),
  has_cover_letter: z.boolean(),
  approval: z
    .object({ approved_at: z.number().int(), stale: z.boolean() })
    .nullable(),
  warm_path: z.object({
    company_tier: z.number().int().nullable(),
    contact_count: z.number().int(),
    last_touch: z.number().int().nullable(),
    recent: z.boolean(),
  }),
});
export type ReviewQueueItem = z.infer<typeof ReviewQueueItemSchema>;

// An open packet whose listing has closed: withdraw it rather than review it.
export const ReviewQueueClosedSchema = z.object({
  application_id: z.string(),
  review_url: z.string(),
  role: z.string(),
  company_name: z.string(),
  status: ApplicationStatusSchema,
  // updated_at, which the listing reconciler stamps when the posting closes.
  closed_at: z.number().int(),
});
export type ReviewQueueClosed = z.infer<typeof ReviewQueueClosedSchema>;

export const ApproveRequestSchema = z.object({
  snapshot_sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const RecordSubmissionRequestSchema = z.object({
  mode: SubmitModeSchema,
  confirmation_url: z.string().url().optional(),
  confirmation_text: z.string().max(4_000).optional(),
});

// ── Standing-answers bank ──────────────────────────────────────────────────
//
// Per-user answers to stable questions that recur on EVERY application form
// regardless of company. Keyed by a canonical standing_key the field
// classifier resolves to (it reuses the classifier's profile_key channel, so
// these keys overlap with profile columns where one exists). The apply kit
// prefills these verbatim when the profile has no structured value — so a
// stable question is copy-paste-ready on the first application at any company.
export const STANDING_KEYS = [
  "work_authorization",
  "requires_sponsorship",
  "willing_to_relocate",
  "notice_period",
  "salary_expectation",
  "pronouns",
  "gender",
  "race_ethnicity",
  "veteran_status",
  "disability_status",
  "lgbtq_status",
  "current_company",
  "current_title",
  // Workday's My Information step asks for a full postal address and a phone
  // device type; held here rather than as profile columns.
  "address_line1",
  "postal_code",
  "country",
  "phone_device_type",
  "country_phone_code",
] as const;
export type StandingKey = (typeof STANDING_KEYS)[number];
export const StandingKeySchema = z.enum(STANDING_KEYS);

export const StandingAnswerSchema = z.object({
  user_id: z.string(),
  standing_key: StandingKeySchema,
  answer: z.string(),
  updated_at: z.number().int(),
});
export type StandingAnswer = z.infer<typeof StandingAnswerSchema>;

// PUT /api/standing-answers body (and the set_standing_answer MCP tool).
export const SetStandingAnswerRequestSchema = z.object({
  standing_key: StandingKeySchema,
  answer: z.string().min(1).max(2000),
});
export type SetStandingAnswerRequest = z.infer<typeof SetStandingAnswerRequestSchema>;

// ── Company recurring-question bank ─────────────────────────────────────────
// Per-user, per-company answers to questions that recur on every form at ONE
// company but don't map to a profile column or standing_key. Keyed by the
// normalized question label so the match survives per-posting field-id churn.
export const CompanyRecurringAnswerSchema = z.object({
  user_id: z.string(),
  company_id: z.string(),
  question_key: z.string(),
  question_label: z.string(),
  answer: z.string(),
  updated_at: z.number().int(),
});
export type CompanyRecurringAnswer = z.infer<typeof CompanyRecurringAnswerSchema>;

// PUT /api/company-answers body (and the set_company_answer MCP tool). The
// server normalizes question_label → question_key; the caller passes the
// human-readable label verbatim as it appears on the form.
export const SetCompanyAnswerRequestSchema = z.object({
  company_id: z.string().min(1),
  question_label: z.string().min(1).max(400),
  answer: z.string().min(1).max(4000),
});
export type SetCompanyAnswerRequest = z.infer<typeof SetCompanyAnswerRequestSchema>;

// ── Campaigns (batch-apply orchestration; MCP-only surface for now) ─────────

// Derived per-item pipeline stage. Status past 'preparing' wins verbatim;
// interested/preparing refine by apply-flow artefacts. 'removed' = the
// underlying application row was deleted after the campaign snapshot.
export const CampaignStageSchema = z.enum([
  "removed",
  "tracked",
  "form_extracted",
  "answers_drafted",
  "answers_accepted",
  "approved",
  "applied",
  "interviewing",
  "offer",
  "rejected",
  "withdrawn",
]);
export type CampaignStage = z.infer<typeof CampaignStageSchema>;

// POST /api/campaigns body (and the start_campaign MCP tool).
export const CreateCampaignRequestSchema = z.object({
  company_id: z.string(),
  // Optional narrowing on top of the company's default_keywords title match.
  q: z.string().optional(),
  min_score: z.number().min(0).max(10).optional(),
  job_ids: z.array(z.string()).max(20).optional(),
  dry_run: z.boolean().optional(),
  title: z.string().max(120).optional(),
});
export type CreateCampaignRequest = z.infer<typeof CreateCampaignRequestSchema>;

export const CampaignSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  company_id: z.string(),
  title: z.string().nullable(),
  // Parsed filter_json targeting snapshot; shape may evolve, so unknown.
  filter: z.unknown(),
  status: z.enum(["active", "archived"]),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignItemSchema = z.object({
  application_id: z.string(),
  // Nulls when the application row was deleted (stage 'removed').
  role: z.string().nullable(),
  url: z.string().nullable(),
  status: ApplicationStatusSchema.nullable(),
  stage: CampaignStageSchema,
  artifacts: z.object({
    resume_version: z.boolean(),
    cover_letter: z.boolean(),
  }),
  added_at: z.number().int(),
});
export type CampaignItem = z.infer<typeof CampaignItemSchema>;

// What the wave-2 ingest pipeline enqueues onto CAREER_PROCESS_QUEUE.
// Identity-only — same shape as JobProcessMessage so retries reload
// from D1.
export const CareerProcessMessageSchema = z.object({
  unitId: z.string(),
  userId: z.string(),
});
export type CareerProcessMessage = z.infer<typeof CareerProcessMessageSchema>;

// Session 5 — ApplicationAgent timeline + agent state.
//
// The Durable Object owns the rich state per application; the D1 row keeps
// a denormalised `agent_state` JSON for cross-application kanban queries.

export const APPLICATION_EVENT_TYPES = [
  "status_changed",
  "note_added",
  "interview_added",
  "contact_added",
  "draft_created",
  "alarm_scheduled",
  "alarm_fired",
  // Session 7 PR3 — outreach helper. Mark-sent on a drafted message inserts
  // one of these onto the application timeline (when a link exists). Payload:
  // { contact_id, contact_name, message_kind, artifact_id }.
  "message_sent",
  // APPLY_FLOW_SPRINTS PR4 — /api/apply/:id/submit (MCP
  // accept_application_answers) stamps answers accepted and inserts one of
  // these when it stamped anything. Payload: { answers_marked, artifact_ids }.
  "answers_accepted",
  // Approval gate — POST /api/apply/:id/approve froze a snapshot. Payload:
  // { approval_id, snapshot_sha256 }.
  "packet_approved",
  // POST /api/apply/:id/record-submission — the approved packet went out.
  // Payload: { approval_id, mode, confirmation_url? }.
  "submission_recorded",
] as const;
export type ApplicationEventType = (typeof APPLICATION_EVENT_TYPES)[number];
export const ApplicationEventTypeSchema = z.enum(APPLICATION_EVENT_TYPES);

export const ApplicationEventSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  user_id: z.string(),
  type: ApplicationEventTypeSchema,
  payload: z.record(z.string(), z.unknown()).nullable(),
  at: z.number().int(),
});
export type ApplicationEvent = z.infer<typeof ApplicationEventSchema>;

// Wire shape for the per-application timeline contacts list. After Session 7
// PR3 this is a JOIN of `contacts` (per-user identity) + `application_contacts`
// (the link table): `id` is the contact's id, `name` is `c.full_name`, `role`
// is `link.role_in_process`. Pre-PR3 callers don't need to change.
export const ApplicationContactSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  user_id: z.string(),
  name: z.string(),
  role: z.string().nullable(),
  email: z.string().nullable(),
  linkedin_url: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type ApplicationContact = z.infer<typeof ApplicationContactSchema>;

// Session 7 PR3 — per-user contact record (lives in `contacts`). One person
// across N applications. `last_contacted_at` is null until the first
// mark-sent. `company_id` is nullable so a recruiter at an unknown company
// doesn't block creation.
export const ContactSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  company_id: z.string().nullable(),
  full_name: z.string(),
  role: z.string().nullable(),
  linkedin_url: z.string().nullable(),
  email: z.string().nullable(),
  notes: z.string().nullable(),
  last_contacted_at: z.number().int().nullable(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type Contact = z.infer<typeof ContactSchema>;

// POST /api/contacts body. company_id is optional — null is valid for
// "unknown company" contacts that surface in an Other group.
export const CreateContactRequestSchema = z.object({
  full_name: z.string().min(1).max(200),
  company_id: z.string().max(120).nullable().optional(),
  role: z.string().max(200).nullable().optional(),
  linkedin_url: z.string().url().nullable().optional(),
  email: z.string().email().nullable().optional(),
  notes: z.string().max(2_000).nullable().optional(),
});
export type CreateContactRequest = z.infer<typeof CreateContactRequestSchema>;

// PATCH /api/contacts/:id body. All fields optional; only present fields update.
export const PatchContactRequestSchema = z.object({
  full_name: z.string().min(1).max(200).optional(),
  company_id: z.string().max(120).nullable().optional(),
  role: z.string().max(200).nullable().optional(),
  linkedin_url: z.string().url().nullable().optional(),
  email: z.string().email().nullable().optional(),
  notes: z.string().max(2_000).nullable().optional(),
});
export type PatchContactRequest = z.infer<typeof PatchContactRequestSchema>;

// POST /api/applications/:id/contacts/:contactId/link body. role_in_process is
// the per-application relationship label ("recruiter", "hiring_manager",
// "reference", or free-text). Distinct from contact.role (their day-job title).
export const LinkContactRequestSchema = z.object({
  role_in_process: z.string().max(120).nullable().optional(),
});
export type LinkContactRequest = z.infer<typeof LinkContactRequestSchema>;

// Session 7 PR3 — message-draft kinds. Each kind binds a system prompt
// (default in src/prompts/draft-message.ts; user override in user_templates)
// and a per-kind word-count band baked into that prompt.
export const MESSAGE_KINDS = [
  "cold_intro_for_referral",
  "post_application_followup",
  "post_interview_thankyou",
  "recruiter_response",
  "reconnect_and_ask",
] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];
export const MessageKindSchema = z.enum(MESSAGE_KINDS);

// POST /api/contacts/:id/messages/draft body. application_id is optional —
// drafts can be scoped to a contact alone (for cold intros without a target
// app) or pinned to a specific application for retrieval context. focus is
// short user steering text rendered into the user prompt as "Candidate-
// provided focus".
export const DraftMessageRequestSchema = z.object({
  kind: MessageKindSchema,
  application_id: z.string().nullable().optional(),
  channel: z.enum(["linkedin", "email"]).nullable().optional(),
  focus: z.string().max(1_000).optional(),
});
export type DraftMessageRequest = z.infer<typeof DraftMessageRequestSchema>;

// What the compose pipeline returns. `model` is the AI Gateway-routed model
// id; `voice_reference_ids` + `career_unit_ids` are the retrieved sources
// that fed Sonnet (drives a "Drew from N past messages" badge in the UI).
export const DraftMessageResponseSchema = z.object({
  body: z.string(),
  kind: MessageKindSchema,
  channel: z.enum(["linkedin", "email"]).nullable(),
  model: z.string(),
  voice_reference_ids: z.array(z.string()),
  career_unit_ids: z.array(z.string()),
  used_template_override: z.boolean(),
});
export type DraftMessageResponse = z.infer<typeof DraftMessageResponseSchema>;

// POST /api/contacts/:id/messages/sent body. Inserts an
// application_artifacts row with field_kind='message' and updates
// contacts.last_contacted_at. application_id (when set) fires a
// `message_sent` event on that application's agent timeline.
export const MarkMessageSentRequestSchema = z.object({
  kind: MessageKindSchema,
  body: z.string().min(1).max(20_000),
  application_id: z.string().nullable().optional(),
  channel: z.enum(["linkedin", "email"]).nullable().optional(),
  edited: z.boolean().optional(),
});
export type MarkMessageSentRequest = z.infer<typeof MarkMessageSentRequestSchema>;

// Persisted user template row. A missing row falls back to the bundled
// default in src/prompts/draft-message.ts.
export const UserTemplateSchema = z.object({
  user_id: z.string(),
  kind: MessageKindSchema,
  system_prompt: z.string(),
  updated_at: z.number().int(),
});
export type UserTemplate = z.infer<typeof UserTemplateSchema>;

// PUT /api/templates/:kind body.
export const PutTemplateRequestSchema = z.object({
  system_prompt: z.string().min(1).max(8_000),
});
export type PutTemplateRequest = z.infer<typeof PutTemplateRequestSchema>;

export const APPLICATION_DRAFT_KINDS = [
  "cover_letter",
  "resume_version",
  "essay_answer",
  "message",
] as const;
export type ApplicationDraftKind = (typeof APPLICATION_DRAFT_KINDS)[number];
export const ApplicationDraftKindSchema = z.enum(APPLICATION_DRAFT_KINDS);

export const ApplicationDraftSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  user_id: z.string(),
  kind: ApplicationDraftKindSchema,
  ref_id: z.string().nullable(),
  preview: z.string().nullable(),
  created_at: z.number().int(),
});
export type ApplicationDraft = z.infer<typeof ApplicationDraftSchema>;

// One scheduled callback the agent will fire later via DO alarm.
// `callback` is a string label the alarm() handler dispatches on — we keep
// this tiny, finite vocabulary so we never have to deserialize arbitrary code.
export const AGENT_CALLBACKS = [
  "send_followup_nudge",
  "send_interview_thankyou",
  "send_post_rejection_retro",
] as const;
export type AgentCallback = (typeof AGENT_CALLBACKS)[number];
export const AgentCallbackSchema = z.enum(AGENT_CALLBACKS);

export const ScheduledActionSchema = z.object({
  id: z.string(),
  label: z.string(),                    // user-visible (e.g. "Follow up at Acme")
  callback: AgentCallbackSchema,
  fire_at: z.number().int(),            // unix-ms
});
export type ScheduledAction = z.infer<typeof ScheduledActionSchema>;

// JSON shape stored in applications.agent_state — the schedule snapshot the
// WS-subscribed Sheet reads via `useAgent`.
export const AgentStateSchema = z.object({
  scheduled: z.array(ScheduledActionSchema),
  next_action_at: z.number().int().nullable(),
});
export type AgentState = z.infer<typeof AgentStateSchema>;

// Request bodies for the new ApplicationAgent routes.

export const SetStatusRequestSchema = z.object({
  status: ApplicationStatusSchema,
  reason: z.string().max(500).optional(),
});
export type SetStatusRequest = z.infer<typeof SetStatusRequestSchema>;

export const AddNoteRequestSchema = z.object({
  text: z.string().min(1).max(4000),
});
export type AddNoteRequest = z.infer<typeof AddNoteRequestSchema>;

export const AddInterviewRequestSchema = z.object({
  label: z.string().min(1).max(120),    // "Phone screen" / "Onsite — system design"
  scheduled_at: z.number().int(),       // unix-ms
  notes: z.string().max(2000).optional(),
});
export type AddInterviewRequest = z.infer<typeof AddInterviewRequestSchema>;

export const AddContactRequestSchema = z.object({
  name: z.string().min(1).max(120),
  role: z.string().max(120).optional(),
  email: z.string().email().optional(),
  linkedin_url: z.string().url().optional(),
  notes: z.string().max(2000).optional(),
});
export type AddContactRequest = z.infer<typeof AddContactRequestSchema>;

export const AddDraftRequestSchema = z.object({
  kind: ApplicationDraftKindSchema,
  ref_id: z.string().optional(),
  preview: z.string().max(500).optional(),
});
export type AddDraftRequest = z.infer<typeof AddDraftRequestSchema>;

export const ScheduleActionRequestSchema = z.object({
  label: z.string().min(1).max(120),
  callback: AgentCallbackSchema,
  fire_at: z.number().int(),
});
export type ScheduleActionRequest = z.infer<typeof ScheduleActionRequestSchema>;

// GET /api/applications/:id/timeline response wraps the event log.
export const ApplicationTimelineSchema = z.object({
  events: z.array(ApplicationEventSchema),
  contacts: z.array(ApplicationContactSchema),
  drafts: z.array(ApplicationDraftSchema),
  agent_state: AgentStateSchema.nullable(),
  last_alarm_at: z.number().int().nullable(),
});
export type ApplicationTimeline = z.infer<typeof ApplicationTimelineSchema>;

// Session 6 PR3 — InterviewPrepAgent.
//
// Per-application chat coaching DO. Two modes: `live` (pre-interview prep,
// system prompt is "coach this candidate") and `debrief` (post-interview
// reflection). The SPA toggles via POST /prep/mark-debrief.

export const INTERVIEW_PREP_MODES = ["live", "debrief"] as const;
export type InterviewPrepMode = (typeof INTERVIEW_PREP_MODES)[number];
export const InterviewPrepModeSchema = z.enum(INTERVIEW_PREP_MODES);

// Single chat turn. `at` is unix-ms at the moment the turn landed in state.
// `retrieved_unit_ids` is populated only on assistant turns (the units we
// fed into that response) so the SPA can render a "Sources" pop-out.
export const InterviewPrepMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  at: z.number().int(),
  retrieved_unit_ids: z.array(z.string()).optional(),
});
export type InterviewPrepMessage = z.infer<typeof InterviewPrepMessageSchema>;

// State broadcast on every setState. The Sheet/InterviewPrep page subscribes
// via the agent-WS bridge and re-renders off this. `pending_turn` lets the
// UI render a "thinking…" indicator without polling.
export const InterviewPrepStateSchema = z.object({
  user_id: z.string().nullable(),
  application_id: z.string().nullable(),
  mode: InterviewPrepModeSchema,
  // Cached from the application + JD on the first turn so we don't reload it
  // every chat call. Cleared on agent destroy.
  role: z.string().nullable(),
  company: z.string().nullable(),
  job_url: z.string().nullable(),
  // Most recently observed interview event timestamp (for the debrief banner).
  interview_at: z.number().int().nullable(),
  messages: z.array(InterviewPrepMessageSchema),
  // Snapshot of the last retrieval for the SPA's "Sources" popover.
  last_retrieved_unit_ids: z.array(z.string()),
  turns_count: z.number().int(),
  // True while a Sonnet call is in flight. Disables the input box.
  pending_turn: z.boolean(),
});
export type InterviewPrepState = z.infer<typeof InterviewPrepStateSchema>;

// POST /api/applications/:id/prep/chat body.
export const PrepChatRequestSchema = z.object({
  message: z.string().min(1).max(4_000),
});
export type PrepChatRequest = z.infer<typeof PrepChatRequestSchema>;

// GET /api/applications/:id/prep/state response — the read-side snapshot
// the InterviewPrep page hydrates with before the WS connects. Cheaper than
// a stub.fetch since the DO might be cold; the WS connection then takes
// over for real-time updates.
export const PrepSessionMetaSchema = z.object({
  application_id: z.string(),
  role: z.string(),
  company: z.string().nullable(),
  job_url: z.string(),
  mode: InterviewPrepModeSchema,
  interview_at: z.number().int().nullable(),
  turns_count: z.number().int(),
  started_at: z.number().int().nullable(),
  last_turn_at: z.number().int().nullable(),
});
export type PrepSessionMeta = z.infer<typeof PrepSessionMetaSchema>;

// Session 7 PR4 — weekly Monday-morning summary surfaced on Home.
//
// `stats_json` is the snapshot the model saw, persisted verbatim. Shape
// mirrors lib/weekly-summary.ts WeeklySummaryStats but the SPA reads it
// loosely (z.unknown) — the narrative is the primary surface; raw stats
// are escape hatches for the dismiss/regenerate UI.

export const WeekActivitySchema = z.object({
  new_applications: z.number().int(),
  status_changes: z.number().int(),
  interviews_added: z.number().int(),
  notes_added: z.number().int(),
  messages_sent: z.number().int(),
});
export type WeekActivity = z.infer<typeof WeekActivitySchema>;

export const WeeklySummaryStatsSchema = z.object({
  generated_at: z.number().int(),
  week_start: z.number().int(),
  week_end: z.number().int(),
  funnel: z.record(z.string(), z.number()),
  overall: z.object({
    submitted: z.number().int(),
    responded: z.number().int(),
    rate: z.number(),
  }),
  stuck_count: z.number().int(),
  progressed_count: z.number().int(),
  this_week: WeekActivitySchema,
  prev_week: WeekActivitySchema,
});
export type WeeklySummaryStats = z.infer<typeof WeeklySummaryStatsSchema>;

export const WeeklySummarySchema = z.object({
  id: z.string(),
  week_start: z.number().int(),
  narrative: z.string(),
  model: z.string(),
  created_at: z.number().int(),
  dismissed_at: z.number().int().nullable(),
  stats: WeeklySummaryStatsSchema,
});
export type WeeklySummary = z.infer<typeof WeeklySummarySchema>;

// ── MCP prepare/save inversion (Phase 1) ─────────────────────────────────
//
// The prepare_* routes assemble generation context server-side (retrieval,
// prompts, job + resume identity) and mint a 30-min single-use prepare_token;
// the connected model writes the prose and POSTs it back to the matching
// save_* route with the token. Tailor/message prepares reuse
// TailorRequestSchema / DraftMessageRequestSchema verbatim.

// POST /api/summaries/prepare + /api/cover-letter/prepare body.
// `resume_version_id` (cover-letter only) grounds the letter on a tailored
// resume_versions.markdown instead of the master resume text; ignored by the
// summaries route.
export const PrepareGenerationRequestSchema = z.object({
  url: z.string().min(1),
  resume_id: z.string().min(1),
  resume_version_id: z.string().min(1).optional(),
  force: z.boolean().optional(),
  // Cover letters only: extra notes on why this company, added after the
  // company's saved "Why <Company>?" answer (which prepare pulls itself).
  // The letter's "why us" may draw ONLY on these; without either it says
  // nothing about motivation rather than inventing it.
  motivation_notes: z.string().max(4_000).optional(),
});
export type PrepareGenerationRequest = z.infer<typeof PrepareGenerationRequestSchema>;

// POST /api/summaries/save body. `fit` is the client's estimate — the server
// substitutes the catalog gemma score when one exists for (user, job, resume)
// and reports which source won via `fit_source` in the response.
export const SaveFitSummaryRequestSchema = z.object({
  prepare_token: z.string().min(1),
  summary: z.string().min(1).max(2_000),
  fit: z.number().int().min(1).max(10),
  strengths: z.array(z.string()).max(8),
  gaps: z.array(z.string()).max(8),
  verdict: z.string().min(1).max(500),
});
export type SaveFitSummaryRequest = z.infer<typeof SaveFitSummaryRequestSchema>;

// POST /api/cover-letter/save body. Bounds are generous slack around the
// prompt's 250-350 word target.
export const SaveCoverLetterRequestSchema = z.object({
  prepare_token: z.string().min(1),
  text: z.string().min(200).max(6_000),
  // Bypass the save-time quality gate's errors (ungrounded numbers, banned
  // phrases). Only when the user confirmed a flagged item is correct.
  skip_quality_gate: z.boolean().optional(),
});
export type SaveCoverLetterRequest = z.infer<typeof SaveCoverLetterRequestSchema>;

// POST /api/resume-versions/save body. source_unit_ids must be a subset of
// the prepare stash's available_unit_ids (409 subset_violation otherwise).
export const SaveResumeVersionRequestSchema = z.object({
  prepare_token: z.string().min(1),
  markdown: z.string().min(1),
  source_unit_ids: z.array(z.string()).min(1),
  label: z.string().min(1).max(120).optional(),
  // Bypass the save-time quality gate's errors (ungrounded numbers,
  // pronouns, banned phrases). Only when the user confirmed a flagged item
  // is correct.
  skip_quality_gate: z.boolean().optional(),
});
export type SaveResumeVersionRequest = z.infer<typeof SaveResumeVersionRequestSchema>;

// POST /api/resume-versions/:id/attach body. Links (or, with null, unlinks) a
// saved resume version to an application after the fact — for versions saved
// without an application_id at prepare time, so get_apply_kit can surface them
// as the attached resume.
export const AttachResumeVersionRequestSchema = z.object({
  application_id: z.string().min(1).nullable(),
});
export type AttachResumeVersionRequest = z.infer<typeof AttachResumeVersionRequestSchema>;

// Worker /api/health

export const HealthSchema = z.object({
  ok: z.boolean(),
  service: z.string(),
  environment: z.string(),
});
export type Health = z.infer<typeof HealthSchema>;
